"use client"

import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"

/**
 * Claude-like animated thinking indicator.
 * Shows an animated sparkle/orb icon that pulses and rotates while the AI is working.
 * Used as the assistant avatar during streaming.
 */

interface ThinkingIndicatorProps {
  size?: "sm" | "md"
  className?: string
}

export function ThinkingIndicator({ size = "md", className }: ThinkingIndicatorProps) {
  const dim = size === "sm" ? "h-5 w-5" : "h-8 w-8"
  const orbDim = size === "sm" ? "h-2.5 w-2.5" : "h-4 w-4"

  return (
    <div
      className={cn(
        "relative flex items-center justify-center rounded-full shrink-0",
        "bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5",
        dim,
        className
      )}
    >
      {/* Rotating outer ring */}
      <div className={cn("absolute inset-0 rounded-full animate-[thinking-spin_3s_linear_infinite]")}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary/60" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0.5 h-0.5 rounded-full bg-primary/30" />
      </div>
      {/* Pulsing core orb */}
      <div
        className={cn(
          "rounded-full bg-primary/80 animate-[thinking-pulse_1.5s_ease-in-out_infinite]",
          orbDim
        )}
      />
    </div>
  )
}

/**
 * Streaming status text that appears character-by-character like Claude.
 * Shows what the AI is currently doing (e.g. "Searching recipients...")
 */

interface StreamingStatusProps {
  toolName?: string
  isThinking?: boolean
  compact?: boolean
}

const thinkingPhrases = [
  "Thinking...",
  "Working on it...",
  "Analyzing...",
  "Processing...",
]

export function StreamingStatus({ toolName, isThinking, compact }: StreamingStatusProps) {
  // Rotate phrase via ticker (every 4s) so render stays pure — Date.now() in render
  // is impure. When not thinking, ticker stays idle.
  const [phraseIdx, setPhraseIdx] = useState(0)
  useEffect(() => {
    if (!isThinking || toolName) return
    const id = setInterval(
      () => setPhraseIdx((i) => (i + 1) % thinkingPhrases.length),
      4000,
    )
    return () => clearInterval(id)
  }, [isThinking, toolName])

  const text = toolName
    ? toolName
    : isThinking
      ? thinkingPhrases[phraseIdx]
      : "Thinking..."

  return (
    <div
      className={cn(
        "flex items-center gap-2",
        compact ? "px-2 py-0.5" : "px-4 py-1"
      )}
    >
      <ThinkingIndicator size={compact ? "sm" : "md"} />
      <span
        className={cn(
          "text-muted-foreground animate-[thinking-text_2s_ease-in-out_infinite]",
          compact ? "text-[11px]" : "text-xs"
        )}
      >
        {text}
      </span>
    </div>
  )
}
